// createVendorHandlers factory — produces { add, update, delete } handler sets
// for lenders, titleCompanies, and realtors (called three times in App.js).
// For TAWs, writes route to the parent TW so the team vendor pool stays canonical;
// see src/hooks/vendorTargetWorkspace.js.

import * as workspaceService from '../services/workspaceService';
import { formatPhoneNumber } from '../utils/helpers';
import { resolveVendorTargetWorkspaceId } from './vendorTargetWorkspace';

// Per-collection payload options: the realtor list carries coordinators and team
// fields; lenders and title companies are plain.
const VENDOR_OPTIONS = {
    lenders: {},
    titleCompanies: {},
    realtors: {
        childFieldName: 'transactionCoordinators',
        maxChildren: 5,
        additionalStringFields: ['teamId', 'teamName'],
    },
};

function sanitizeVendorItem(item = {}, options = {}) {
    const { childFieldName = null, maxChildren = 5, additionalStringFields = [] } = options;
    const payload = {
        firstName: (item.firstName || '').trim(),
        lastName: (item.lastName || '').trim(),
        company: (item.company || '').trim(),
        email: (item.email || '').trim(),
        phone: (item.phone || '').trim() ? formatPhoneNumber(item.phone) : '',
        phoneExtension: (item.phoneExtension || '').trim()
    };
    if (childFieldName) {
        const source = Array.isArray(item[childFieldName]) ? item[childFieldName].slice(0, maxChildren) : [];
        const normalizedChildren = source
            .map(child => {
                const firstName = (child?.firstName || '').trim();
                const lastName = (child?.lastName || '').trim();
                const email = (child?.email || '').trim();
                const rawPhone = (child?.phone || '').trim();
                const phone = rawPhone ? formatPhoneNumber(rawPhone) : '';
                return { firstName, lastName, email, phone };
            })
            .filter(child => child.firstName || child.lastName || child.email || child.phone);
        payload[childFieldName] = normalizedChildren;
    }
    if (Array.isArray(additionalStringFields) && additionalStringFields.length > 0) {
        additionalStringFields.forEach(fieldName => {
            const rawValue = item[fieldName];
            if (typeof rawValue === 'string') {
                payload[fieldName] = rawValue.trim();
            } else if (rawValue == null) {
                payload[fieldName] = '';
            }
        });
    }
    return payload;
}

export function useVendorHandlers({
    selectedWorkspace,
    run,
    showToast,
}) {
    const targetWorkspaceId = resolveVendorTargetWorkspaceId(selectedWorkspace);
    const isTaw = selectedWorkspace?.teamRole === 'team_agent';
    const tawMissingParent = isTaw && !targetWorkspaceId;

    const createVendorHandlers = (collectionName, singularName, options = {}) => {
        const sanitizeVendorPayload = (item = {}) => sanitizeVendorItem(item, options);

        return {
            add: async (item) => {
                if (tawMissingParent) {
                    showToast(`Cannot save: team workspace not linked.`, 'error');
                    return null;
                }
                if (!targetWorkspaceId) return null;
                const payload = sanitizeVendorPayload(item);
                const result = await run(async () => {
                    const docRef = await workspaceService.createVendor(targetWorkspaceId, collectionName, payload);
                    showToast(`${singularName} added successfully!`);
                    return docRef.id;
                }, `Failed to add ${singularName}.`);
                return result ?? null;
            },
            update: async (id, item) => {
                if (tawMissingParent) {
                    showToast(`Cannot save: team workspace not linked.`, 'error');
                    return;
                }
                if (!targetWorkspaceId) return;
                const payload = sanitizeVendorPayload(item);
                await run(async () => {
                    await workspaceService.updateVendor(targetWorkspaceId, collectionName, id, payload);
                    showToast(`${singularName} updated successfully!`);
                }, `Failed to update ${singularName}.`);
            },
            delete: async (id) => {
                if (tawMissingParent) {
                    showToast(`Cannot save: team workspace not linked.`, 'error');
                    return false;
                }
                if (!targetWorkspaceId || !window.confirm(`Delete this ${singularName}?`)) return false;
                const result = await run(async () => {
                    await workspaceService.deleteVendor(targetWorkspaceId, collectionName, id);
                    showToast(`${singularName} deleted.`);
                    return true;
                }, `Failed to delete ${singularName}.`);
                return result ?? false;
            },
        };
    };

    const lenderHandlers = createVendorHandlers("lenders", "Lender");
    const titleCompanyHandlers = createVendorHandlers("titleCompanies", "Title Company");
    const realtorHandlers = createVendorHandlers("realtors", "Realtor", VENDOR_OPTIONS.realtors);

    // Phase 72 (D-18, D-23): headless create-if-absent at a deterministic id, for
    // the contract setup card. Writes to the parent workspace for a team agent
    // workspace (the same target as the Business Partners add) and returns
    // { id, created }, or null when there is nowhere to write.
    const createVendorOnce = async (collectionName, { id, item } = {}) => {
        if (!VENDOR_OPTIONS[collectionName] || !id) return null;
        if (tawMissingParent || !targetWorkspaceId) return null;
        const payload = sanitizeVendorItem(item, VENDOR_OPTIONS[collectionName]);
        return workspaceService.createVendorOnce(targetWorkspaceId, collectionName, id, payload);
    };

    return { lenderHandlers, titleCompanyHandlers, realtorHandlers, createVendorOnce };
}
